interface ToastOptions {
  message: string;
  type?: ToastType;
  duration?: number;
}

type ToastType = 'success' | 'error' | 'info' | 'warning';

class Toast {
  private container: HTMLDivElement | null = null;

  private createContainer() {
    if (this.container) return this.container;

    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999999;
    `;
    document.body.appendChild(container);
    this.container = container;
    return container;
  }

  private createToastElement(options: ToastOptions): HTMLDivElement {
    const { message, type = 'info' } = options;
    const element = document.createElement('div');

    element.style.cssText = `
      margin-bottom: 8px;
      padding: 10px 15px;
      border-radius: 4px;
      font-size: 13px;
      transition: all 0.3s;
      opacity: 0;
      transform: translateY(-20px);
      display: inline-flex;
      align-items: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      white-space: nowrap;
    `;

    // 降低背景透明度到 0.3
    const colors = {
      success: 'rgba(103, 194, 58, 0.4)',
      error: 'rgba(245, 108, 108, 0.4)',
      info: 'rgba(144, 147, 153, 0.6)',
      warning: 'rgba(230, 162, 60, 0.4)'
    };

    element.style.backgroundColor = colors[type];
    element.style.color = '#ffffff';

    // 添加圖標
    const icon = this.createIcon(type);
    element.appendChild(icon);

    // 添加文字
    const messageElement = document.createElement('span');
    messageElement.style.marginLeft = '10px';
    messageElement.textContent = message;
    element.appendChild(messageElement);

    return element;
  }

  private createIcon(type: ToastType): HTMLSpanElement {
    const span = document.createElement('span');
    const icons = {
      success: '✓',
      error: '✕',
      info: 'ℹ',
      warning: '⚠'
    };
    span.textContent = icons[type];
    return span;
  }

  private animateIn(element: HTMLDivElement) {
    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'translateY(0)';
    });
  }

  private animateOut(element: HTMLDivElement) {
    element.style.opacity = '0';
    element.style.transform = 'translateY(-20px)';

    element.addEventListener('transitionend', () => {
      element.remove();
    });
  }

  show(options: ToastOptions) {
    const container = this.createContainer();
    const element = this.createToastElement(options);
    container.appendChild(element);

    // 顯示動畫
    this.animateIn(element);

    // 設定自動關閉
    const duration = options.duration ?? 3000;
    setTimeout(() => {
      this.animateOut(element);
    }, duration);
  }

  success(message: string, duration?: number) {
    this.show({ message, type: 'success', duration });
  }

  error(message: string, duration?: number) {
    this.show({ message, type: 'error', duration });
  }

  info(message: string, duration?: number) {
    this.show({ message, type: 'info', duration });
  }

  warning(message: string, duration?: number) {
    this.show({ message, type: 'warning', duration });
  }
}

export const toast = new Toast();
